import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, Loader2, DollarSign } from "lucide-react";

/* ============ PROMOTIONS PANEL ============ */
export default function PromotionsPanel({ promoCodes, categories, products, onRefresh }) {
  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    value: 10,
    min_order_amount: 0,
    category_id: "",
    product_id: "",
    use_date_range: true,
    starts_at: "",
    ends_at: "",
    active: true,
  });
  const [saving, setSaving] = useState(false);
  const [localPromos, setLocalPromos] = useState(promoCodes || []);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  useEffect(() => {
    setLocalPromos(promoCodes || []);
  }, [promoCodes]);

  const now = new Date();
  const isExpired = (promo) => {
    if (!promo?.ends_at) return false;
    const d = new Date(promo.ends_at);
    return !Number.isNaN(d.getTime()) && d < now;
  };

  const activePromos = (localPromos || []).filter((p) => !isExpired(p));
  const expiredPromos = (localPromos || []).filter((p) => isExpired(p));

  const formatDate = (value, fallback = "Ingen") => {
    if (!value) return fallback;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });
  };

  const openDatePicker = (ref) => {
    try {
      ref?.current?.showPicker?.();
    } catch (e) {
      ref?.current?.focus?.();
    }
  };

  const normalizedCode = form.code.trim().toUpperCase();
  const selectableCategories = (categories || []).filter((c) => c.active !== false);
  const productsInSelectedCategory = (products || []).filter(
    (p) => form.category_id && p.active !== false && p.category_id === form.category_id
  );
  const valueIsValid = Number(form.value) > 0;
  const minOrderIsValid = Number(form.min_order_amount) >= 0;
  const datesFilled = Boolean(form.starts_at && form.ends_at);
  const datesOrdered = !form.starts_at || !form.ends_at || form.ends_at >= form.starts_at;
  const dateRangeIsValid = !form.use_date_range || (datesFilled && datesOrdered);
  const canCreatePromo = Boolean(normalizedCode) && valueIsValid && minOrderIsValid && dateRangeIsValid && !saving;

  const handleCreate = async () => {
    const cleanCode = normalizedCode;
    if (!cleanCode) {
      toast.error("Rabattkod krävs");
      return;
    }
    if ((localPromos || []).some((p) => String(p.code || "").toUpperCase() === cleanCode)) {
      toast.error("Rabattkoden finns redan. Välj en annan kod.");
      return;
    }
    if (Number(form.value) <= 0) {
      toast.error("Rabattvärde måste vara större än 0");
      return;
    }
    if (form.use_date_range && (!form.starts_at || !form.ends_at)) {
      toast.error("Välj både startdatum och slutdatum, eller stäng av datumperiod");
      return;
    }
    if (form.use_date_range && form.starts_at && form.ends_at && form.ends_at < form.starts_at) {
      toast.error("Slutdatum måste vara efter startdatum");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post("/admin/promo-codes", {
        code: cleanCode,
        discount_type: form.discount_type,
        value: Number(form.value),
        min_order_amount: Number(form.min_order_amount) || 0,
        category_id: form.category_id || null,
        product_id: form.product_id || null,
        starts_at: form.use_date_range && form.starts_at ? `${form.starts_at}T00:00:00+00:00` : null,
        ends_at: form.use_date_range && form.ends_at ? `${form.ends_at}T23:59:59+00:00` : null,
        active: form.active,
      });

      // Instant UI update in the tab (no wait for global refetch)
      setLocalPromos((prev) => [res.data, ...prev.filter((p) => p.id !== res.data.id)]);
      setForm({
        code: "",
        discount_type: "percent",
        value: 10,
        min_order_amount: 0,
        category_id: "",
        product_id: "",
        use_date_range: true,
        starts_at: "",
        ends_at: "",
        active: true,
      });
      toast.success("Rabattkod skapad");
      onRefresh();
    } catch (e) {
      const detail = String(e.response?.data?.detail || "");
      if (detail.toLowerCase().includes("already exists")) {
        toast.error("Rabattkoden finns redan. Välj en annan kod.");
      } else {
        try {
          const listRes = await api.get("/admin/promo-codes");
          const existingPromo = (listRes.data || []).find((p) => String(p.code || "").toUpperCase() === cleanCode);
          if (existingPromo) {
            setLocalPromos(listRes.data || []);
            toast.success("Rabattkod skapad");
            return;
          }
        } catch (_ignored) {
          // Ignore reconciliation errors and show the original error below.
        }
        toast.error(detail || "Kunde inte skapa rabattkod");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (promo) => {
    try {
      const res = await api.put(`/admin/promo-codes/${promo.id}`, { active: !promo.active });
      setLocalPromos((prev) => prev.map((p) => (p.id === promo.id ? res.data : p)));
      toast.success(!promo.active ? "Rabattkod aktiverad" : "Rabattkod pausad");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte uppdatera rabattkod");
    }
  };

  const handleDelete = async (promo, permanent = false) => {
    const confirmText = permanent
      ? `Radera rabattkod permanent: ${promo.code}?`
      : `Avsluta rabattkod ${promo.code}? Den flyttas till Utgångna rabattkoder.`;
    if (!window.confirm(confirmText)) return;
    try {
      const res = await api.delete(`/admin/promo-codes/${promo.id}`, {
        params: { permanent: permanent ? "true" : "false" },
      });
      if (permanent) {
        setLocalPromos((prev) => prev.filter((p) => p.id !== promo.id));
        toast.success("Rabattkod raderad permanent");
      } else {
        setLocalPromos((prev) => prev.map((p) => (p.id === promo.id ? res.data : p)));
        toast.success("Rabattkod flyttad till utgångna");
      }
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte uppdatera rabattkod");
    }
  };

  return (
    <div className="max-w-4xl" data-testid="promotions-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-playfair text-xl font-bold text-[#EDEDED]">Kampanjer & rabattkoder</h2>
          <p className="text-[#E8E8EA] text-xs mt-1">Skapa kampanjer som kunder kan använda i checkout</p>
        </div>
      </div>

      <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4 mb-5">
        <h3 className="text-sm font-semibold text-[#EDEDED]">Skapa ny rabattkod</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <Input
            value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
            placeholder="t.ex. VINTER10"
            className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
          />

          <div className="col-span-1 sm:col-span-2 lg:col-span-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, discount_type: "percent" }))}
                className={`h-10 rounded-md border text-xs font-semibold transition-all ${form.discount_type === "percent" ? "border-[#D4B483] bg-[#D4B483]/15 text-[#D4B483]" : "border-[#27272A] bg-[#0A0A0A] text-[#E8E8EA]"}`}
              >
                Procent (%)
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, discount_type: "fixed" }))}
                className={`h-10 rounded-md border text-xs font-semibold transition-all ${form.discount_type === "fixed" ? "border-[#D4B483] bg-[#D4B483]/15 text-[#D4B483]" : "border-[#27272A] bg-[#0A0A0A] text-[#E8E8EA]"}`}
              >
                Fast belopp (kr)
              </button>
            </div>
            <p className="text-xs text-[#E8E8EA] mt-1">
              {form.discount_type === "percent"
                ? "Procent: skriv t.ex. 10 för 10% rabatt"
                : "Fast belopp: skriv t.ex. 50 för 50 kr rabatt"}
            </p>
          </div>

          <div>
            <Input
              type="number"
              min="1"
              value={form.value}
              onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
              placeholder={form.discount_type === "percent" ? "Rabattvärde (%), t.ex. 10" : "Rabattvärde (kr), t.ex. 50"}
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
            />
            <p className="text-xs text-[#E8E8EA] mt-1">
              {form.discount_type === "percent"
                ? "Detta är hur många procent som dras av"
                : "Detta är hur många kronor som dras av"}
            </p>
          </div>

          <div>
            <Input
              type="number"
              min="0"
              value={form.min_order_amount}
              onChange={e => setForm(f => ({ ...f, min_order_amount: e.target.value }))}
              placeholder="Minsta order (kr), t.ex. 200"
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
            />
            <p className="text-xs text-[#E8E8EA] mt-1">Valfritt: skriv 0 om koden ska fungera på alla orderbelopp</p>
          </div>

          <Button onClick={handleCreate} disabled={!canCreatePromo} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-10 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Skapa"}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kategori (valfritt)</Label>
            <select
              value={form.category_id}
              onChange={e => {
                const categoryId = e.target.value;
                setForm((f) => ({ ...f, category_id: categoryId, product_id: "" }));
              }}
              className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] h-10 rounded-md px-3"
            >
              <option value="">Hela menyn</option>
              {selectableCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-xs text-[#E8E8EA] mt-1">Välj kategori för kategorirabatt, eller lämna som hela menyn</p>
          </div>
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Produkt i vald kategori (valfritt)</Label>
            <select
              value={form.product_id}
              onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}
              disabled={!form.category_id}
              className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] h-10 rounded-md px-3 disabled:opacity-50"
            >
              <option value="">{form.category_id ? "Hela kategorin" : "Välj kategori först"}</option>
              {productsInSelectedCategory.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <p className="text-xs text-[#E8E8EA] mt-1">Välj produkt för produktrabatt. Lämna tomt för hela vald kategori.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex items-center justify-between rounded-md border border-[#27272A] bg-[#0A0A0A] px-3 py-2">
            <div>
              <p className="text-xs font-medium text-[#EDEDED]">Använd datumperiod</p>
              <p className="text-xs text-[#E8E8EA]">Om på måste start- och slutdatum fyllas i. Om av gäller koden utan datum.</p>
            </div>
            <Switch
              checked={form.use_date_range}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, use_date_range: checked }))}
            />
          </div>
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Startdatum</Label>
            <div className="flex gap-2">
              <Input
                ref={startDateRef}
                type="date"
                value={form.starts_at}
                onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))}
                disabled={!form.use_date_range}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
              />
              <Button type="button" variant="outline" onClick={() => openDatePicker(startDateRef)} disabled={!form.use_date_range} className="border-[#27272A] text-[#EDEDED] h-10 px-3">📅</Button>
            </div>
          </div>
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Slutdatum</Label>
            <div className="flex gap-2">
              <Input
                ref={endDateRef}
                type="date"
                value={form.ends_at}
                onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))}
                disabled={!form.use_date_range}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
              />
              <Button type="button" variant="outline" onClick={() => openDatePicker(endDateRef)} disabled={!form.use_date_range} className="border-[#27272A] text-[#EDEDED] h-10 px-3">📅</Button>
            </div>
          </div>
        </div>

        <p className="text-xs text-[#E8E8EA]">
          För att skapa rabattkod måste kod och rabattvärde fyllas i. Om datumperiod är på är både start- och slutdatum obligatoriska.
        </p>
      </div>

      <div className="mb-5">
        <h3 className="text-sm font-semibold text-[#10B981] mb-3">Aktiva rabattkoder ({activePromos.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {activePromos.map(promo => (
            <div key={promo.id} className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-lg font-bold text-[#EDEDED]">{promo.code}</p>
                  <p className="text-xs text-[#E8E8EA] mt-1">
                    {promo.discount_type === "percent" ? `${promo.value}% rabatt` : `${promo.value} kr rabatt`}
                    {promo.min_order_amount ? ` · Min ${promo.min_order_amount} kr` : ""}
                  </p>
                  {promo.product_name && (
                    <p className="text-xs text-[#93C5FD] mt-1">Gäller endast: {promo.product_name}</p>
                  )}
                  {!promo.product_name && promo.category_name && (
                    <p className="text-xs text-[#93C5FD] mt-1">Gäller kategori: {promo.category_name}</p>
                  )}
                  <p className="text-xs text-[#E8E8EA] mt-1">
                    Start: {formatDate(promo.starts_at, "Ingen")} · Slut: {formatDate(promo.ends_at, "Ingen")}
                  </p>
                </div>
                <Badge className={promo.active ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#6B7280]/15 text-[#E8E8EA]"}>
                  {promo.active ? "Aktiv" : "Pausad"}
                </Badge>
              </div>
              <div className="text-xs text-[#E8E8EA] mb-3">
                Använd: {promo.used_count || 0}{promo.usage_limit ? ` / ${promo.usage_limit}` : ""}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleToggle(promo)} className="flex-1 border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]">
                  {promo.active ? "Pausa" : "Aktivera"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(promo)} className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        {activePromos.length === 0 && (
          <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 text-center">
            <p className="text-[#E8E8EA] text-sm">Inga aktiva rabattkoder just nu</p>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-[#EF4444] mb-3">Utgångna rabattkoder ({expiredPromos.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {expiredPromos.map(promo => (
            <div key={promo.id} className="bg-[#121212] rounded-xl border border-[#3F1D1D] p-4 opacity-90">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-lg font-bold text-[#EDEDED]">{promo.code}</p>
                  <p className="text-xs text-[#E8E8EA] mt-1">
                    {promo.discount_type === "percent" ? `${promo.value}% rabatt` : `${promo.value} kr rabatt`}
                  </p>
                  {promo.product_name && (
                    <p className="text-xs text-[#93C5FD] mt-1">Gäller endast: {promo.product_name}</p>
                  )}
                  {!promo.product_name && promo.category_name && (
                    <p className="text-xs text-[#93C5FD] mt-1">Gäller kategori: {promo.category_name}</p>
                  )}
                  <p className="text-xs text-[#FCA5A5] mt-1">
                    Start: {formatDate(promo.starts_at, "Ingen")} · Gick ut: {formatDate(promo.ends_at, "Ingen")}
                  </p>
                </div>
                <Badge className="bg-[#EF4444]/15 text-[#EF4444]">Utgången</Badge>
              </div>
              <div className="text-xs text-[#E8E8EA] mb-3">
                Använd: {promo.used_count || 0}{promo.usage_limit ? ` / ${promo.usage_limit}` : ""}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleDelete(promo, true)} className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Radera permanent
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {localPromos.length === 0 && (
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-8 text-center mt-4">
          <DollarSign className="w-10 h-10 text-[#E8E8EA] mx-auto mb-3 opacity-50" />
          <p className="text-[#E8E8EA] text-sm">Inga rabattkoder skapade än</p>
        </div>
      )}
    </div>
  );
}
